/**
 * FreeAgent OAuth2 Authentication
 * 
 * Handles OAuth2 authorization flow for FreeAgent connectivity
 */

import { freeagentConfig } from '../config/freeagentConfig';
import { freeAgentHttpClient } from './httpClient';

// Re-export for convenience
export { isFreeAgentConfigured, isFreeAgentSyncEnabled } from '../config/freeagentConfig';

/**
 * Generate OAuth2 authorization URL
 * Redirects user to FreeAgent to authorize the application
 */
export function getAuthorizationUrl(redirectUri: string): string {
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: freeagentConfig.clientId,
    redirect_uri: redirectUri,
  });

  return `https://api.freeagent.com/v2/approve_app?${params.toString()}`;
}

/**
 * Get the redirect URI for FreeAgent OAuth callback
 * 
 * IMPORTANT: The redirect URI must be pre-registered in your FreeAgent OAuth app settings.
 * FreeAgent will reject redirects to any URI not in the allowlist.
 * 
 * For production, you must add the production redirect URI to your FreeAgent OAuth app:
 * https://[your-production-domain]/integrations/freeagent/callback
 * 
 * NOTE: In Vercel (or any Create React App environment), client-side environment variables
 * MUST use the REACT_APP_ prefix to be embedded in the build. Set REACT_APP_FA_REDIRECT_URI
 * in your Vercel environment variables.
 */
export function getRedirectUri(): string {
  // Allow override via environment variable (recommended for production)
  // Check both REACT_APP_FA_REDIRECT_URI (correct) and FA_REDIRECT_URI (won't work in browser, but helpful for error message)
  const envRedirectUri = process.env.REACT_APP_FA_REDIRECT_URI || process.env.FA_REDIRECT_URI;
  
  if (envRedirectUri) {
    // Remove @ prefix if present (Vercel sometimes adds this)
    const cleanUri = envRedirectUri.replace(/^@/, '');
    
    // Warn if using FA_REDIRECT_URI without REACT_APP_ prefix (won't work in browser)
    if (process.env.FA_REDIRECT_URI && !process.env.REACT_APP_FA_REDIRECT_URI) {
      console.warn('⚠️ WARNING: FA_REDIRECT_URI is set but REACT_APP_FA_REDIRECT_URI is not.', {
        message: 'In Create React App, client-side environment variables MUST use the REACT_APP_ prefix.',
        suggestion: 'Rename FA_REDIRECT_URI to REACT_APP_FA_REDIRECT_URI in your Vercel environment variables.',
        currentValue: cleanUri,
      });
    }
    
    return cleanUri;
  }
  
  if (typeof window === 'undefined') {
    // Server-side rendering fallback
    return 'http://localhost:3000/integrations/freeagent/callback';
  }
  
  const redirectUri = `${window.location.origin}/integrations/freeagent/callback`;
  
  // Warn if we're in production but might be using localhost
  if (process.env.NODE_ENV === 'production' && redirectUri.includes('localhost')) {
    console.warn('⚠️ WARNING: Using localhost redirect URI in production!', {
      redirectUri,
      suggestion: 'Set REACT_APP_FA_REDIRECT_URI environment variable in Vercel to your production URL (e.g., https://jarvis.keystonepropertysouth.co.uk/integrations/freeagent/callback), or ensure your FreeAgent OAuth app has the correct redirect URI registered.',
    });
  }
  
  return redirectUri;
}

/**
 * Initiate OAuth2 authorization flow
 * Redirects user to FreeAgent authorization page
 */
export function initiateAuthorization(): void {
  if (!freeagentConfig.clientId) {
    throw new Error('FreeAgent client ID is not configured');
  }

  const redirectUri = getRedirectUri();
  console.log('🔗 FreeAgent OAuth - Redirect URI:', redirectUri);
  console.log('📝 IMPORTANT: Ensure this exact URI is registered in your FreeAgent OAuth app settings:', redirectUri);
  
  const authUrl = getAuthorizationUrl(redirectUri);
  
  // Store redirect URI in sessionStorage for callback verification
  sessionStorage.setItem('freeagent_redirect_uri', redirectUri);
  
  // Redirect to FreeAgent
  window.location.href = authUrl;
}

/**
 * Handle OAuth2 callback
 * Exchanges authorization code for access token
 */
export async function handleCallback(code: string, state?: string): Promise<{ success: boolean; error?: string }> {
  try {
    const redirectUri = sessionStorage.getItem('freeagent_redirect_uri') || getRedirectUri();
    
    // Exchange code for token
    await freeAgentHttpClient.exchangeCodeForToken(code, redirectUri);
    
    // Clear stored redirect URI
    sessionStorage.removeItem('freeagent_redirect_uri');
    
    return { success: true };
  } catch (error: any) {
    console.error('FreeAgent OAuth callback error:', error);
    return {
      success: false,
      error: error.message || 'Failed to complete FreeAgent authorization',
    };
  }
}

/**
 * Check if user is authenticated with FreeAgent
 */
export async function isAuthenticated(): Promise<boolean> {
  try {
    await freeAgentHttpClient.getAccessToken();
    return true;
  } catch {
    return false;
  }
}

/**
 * Disconnect from FreeAgent (clear tokens)
 */
export function disconnect(): void {
  if (typeof window === 'undefined') return;
  
  localStorage.removeItem(freeagentConfig.tokenStoreKey);
  localStorage.removeItem(freeagentConfig.refreshTokenStoreKey);
  localStorage.removeItem(freeagentConfig.tokenExpiryStoreKey);
  sessionStorage.removeItem('freeagent_redirect_uri');
}

